import {
  createLatexDecorations,
  findLatexMarkersInTextNodes,
  findLatexPairs,
  getLatexMathParser,
} from "./utils";

import type { LatexPair } from "./types";
import type { PendingConversion } from "../LatexInput";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { Decoration } from "@tiptap/pm/view";

export function processCurrentTextBlock(
  blockNode: ProseMirrorNode,
  blockStart: number,
  blockEnd: number,
  cursorPos: number,
  endPos: number,
  doc: ProseMirrorNode,
  docModel: DocumentModel,
): {
  decorations: Decoration[];
  activeInput: LatexPair | null;
  pendingConversions: PendingConversion[];
} {
  const markers = findLatexMarkersInTextNodes(blockNode, blockStart);

  if (markers.length === 0) {
    return { decorations: [], activeInput: null, pendingConversions: [] };
  }

  const pairs = findLatexPairs(markers, doc, blockEnd, endPos);
  const decorations: Decoration[] = [];
  const pendingConversions: PendingConversion[] = [];
  let activeInput: LatexPair | null = null;

  for (const pair of pairs) {
    const { start, end, content } = pair;

    const pairStart = start.pos;
    const pairEnd = end ? end.pos + end.type.length : blockEnd;

    const isCursorInside = cursorPos > pairStart && cursorPos <= pairEnd;

    // Check if we should queue conversion when cursor moves outside a complete pair
    if (end && (cursorPos > pairEnd || cursorPos < pairStart)) {
      let focusTarget: "after" | "before" | null = null;
      if (cursorPos === pairEnd) {
        focusTarget = "after";
      } else if (cursorPos === pairStart) {
        focusTarget = "before";
      }

      if (content.trim()) {
        // Validate LaTeX parse (side-effect-free)
        const parseMathLatex = getLatexMathParser();
        let hasError = false;

        if (parseMathLatex) {
          try {
            parseMathLatex(content);
            // Valid LaTeX - queue for conversion
            pendingConversions.push({
              content,
              start,
              end,
              focusTarget,
            });
          } catch {
            hasError = true;
          }
        }

        decorations.push(...createLatexDecorations({ pair, isCursorInside, hasError }));
        continue;
      }
    }

    decorations.push(
      ...createLatexDecorations({
        pair,
        isCursorInside,
        activeSuggestions: docModel.editor.suggestions.mode === "active",
      }),
    );

    if (isCursorInside) {
      activeInput = pair;
    }
  }

  return { decorations, activeInput, pendingConversions };
}
